import styled from '@emotion/styled';
import {
  ArrowBack,
  ArrowForward,
  Games,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowUp,
  Shuffle,
} from '@mui/icons-material';
import events from '~/modules/GameEvents/GameEvents';
import { useEventListener } from '~/modules/GameEvents/hooks';
import RemoteMicClient from '~/modules/RemoteMic/Network/Client';
import { keyStrokes } from '~/modules/RemoteMic/Network/messages';
import RemoteSongSearch from '~/routes/RemoteMic/Panels/Microphone/RemoteSongSearch';

interface Props {
  onSearchStateChange?: (isActive: boolean) => void;
}

export default function RemoteMicKeyboard({ onSearchStateChange }: Props) {
  const [keyboard] = useEventListener(events.remoteKeyboardLayout, true) ?? [];

  const isHorizontal = keyboard?.horizontal !== undefined || keyboard?.['horizontal-vertical'] !== undefined;
  const isVertical = keyboard?.vertical !== undefined || keyboard?.['horizontal-vertical'] !== undefined;

  const onPress = (key: keyStrokes) => () => {
    // navigator?.vibrate?.(200);
    RemoteMicClient.sendKeyStroke(key);
  };

  return keyboard !== undefined ? (
    <KeyboardContainer data-test="remote-keyboard">
      {keyboard?.remote?.includes('search') && <RemoteSongSearch onSearchStateChange={onSearchStateChange} />}
      {(isHorizontal || isVertical) && (
        <ActionsContainer>
          <ArrowsContainer>
            <ArrowButton
              onClick={onPress('up')}
              disabled={!isVertical}
              data-disabled={!isVertical}
              data-test="arrow-up">
              <KeyboardArrowUp />
            </ArrowButton>
          </ArrowsContainer>
          <ArrowsContainer>
            <ArrowButton
              onClick={onPress('left')}
              disabled={!isHorizontal}
              data-disabled={!isHorizontal}
              data-test="arrow-left">
              <KeyboardArrowLeft />
            </ArrowButton>
            <ArrowButton disabled data-disabled>
              <Games />
            </ArrowButton>
            <ArrowButton
              onClick={onPress('right')}
              disabled={!isHorizontal}
              data-disabled={!isHorizontal}
              data-test="arrow-right">
              <KeyboardArrowRight />
            </ArrowButton>
          </ArrowsContainer>
          <ArrowsContainer>
            <ArrowButton
              onClick={onPress('down')}
              disabled={!isVertical}
              data-disabled={!isVertical}
              data-test="arrow-down">
              <KeyboardArrowDown />
            </ArrowButton>
          </ArrowsContainer>
        </ActionsContainer>
      )}
      <ActionsContainer>
        <ActionButton onClick={onPress('back')} disabled={keyboard?.back === undefined} data-test="keyboard-backspace">
          {keyboard?.back || (
            <>
              <ArrowBack /> Back
            </>
          )}
        </ActionButton>
        <ActionButton onClick={onPress('accept')} disabled={keyboard?.accept === undefined} data-test="keyboard-enter">
          {keyboard?.accept || (
            <>
              Enter <ArrowForward />
            </>
          )}
        </ActionButton>
      </ActionsContainer>
      <Break />
      <ActionsContainer disabled={keyboard?.shiftR === undefined} data-test="keyboard-shift-r">
        <ActionButton onClick={onPress('random')}>
          <Shuffle /> {keyboard?.shiftR || 'Random Song'}
        </ActionButton>
      </ActionsContainer>
    </KeyboardContainer>
  ) : null;
}

const KeyboardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const ArrowsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  color: var(--walky-ink);
`;

const ActionsContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 112px;
  justify-content: center;
  gap: 8px;
  opacity: ${(props) => (props.disabled ? 0 : 1)};
`;

const Break = styled.div`
  flex-basis: 100%;
  height: 0;
`;

const ButtonBase = styled.button<{ disabled?: boolean }>`
  width: 48px;
  height: 48px;
  min-width: 48px;
  box-sizing: border-box;
  border: 1px solid var(--walky-border);
  border-radius: 10px;
  background: var(--walky-surface-raised);
  color: var(--walky-ink);
  box-shadow: var(--walky-shadow);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font: inherit;
  font-size: 14px;
  font-weight: 900;
  transition:
    background 160ms ease,
    border-color 160ms ease,
    color 160ms ease,
    transform 120ms ease;

  svg {
    width: 24px;
    height: 24px;
  }

  &:disabled,
  &[data-disabled='true'] {
    cursor: default;
    opacity: 0.34;
  }
`;

const ArrowButton = styled(ButtonBase)`
  &[data-disabled='false'] {
    cursor: pointer;

    &:active {
      color: #ffffff;
      background: var(--walky-orange);
      border-color: var(--walky-orange);
      transform: translateY(1px);
    }
  }
`;

const ActionButton = styled(ArrowButton)<{ disabled?: boolean }>`
  width: 100%;
  min-width: 0;
  height: 46px;
`;
